// First-visit blocking legal acknowledgement.
//
// hantavirusmap.io is a public-good educational tracker, not a medical service
// and not an authoritative surveillance system. Before letting any user
// interact with the data, we make them acknowledge:
//   1. educational only — not medical advice
//   2. not an official surveillance source
//   3. data may be incomplete, delayed, or wrong
//   4. they accept the privacy + license terms
//
// Acceptance is persisted to localStorage under a versioned key so we can
// re-prompt if the disclaimer materially changes (bump the version).

import { useEffect, useState } from 'react';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './language-switcher';

const STORAGE_KEY = 'hantavirusmap.io:legal-ack:v1';

export function LegalGate() {
  const { t } = useTranslation();
  // Render-gated by hasAck; default true so SSR/no-JS doesn't flash the modal.
  // We flip to false in useEffect when localStorage confirms no acknowledgement.
  const [hasAck, setHasAck] = useState(true);
  const [understandsMedical, setUnderstandsMedical] = useState(false);
  const [understandsSurveillance, setUnderstandsSurveillance] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (!stored) setHasAck(false);
    } catch {
      // localStorage unavailable (private browsing) — show the gate every load.
      setHasAck(false);
    }
  }, []);

  if (hasAck) return null;

  const canAccept = understandsMedical && understandsSurveillance;

  function accept() {
    try {
      window.localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch { /* ignore — private browsing */ }
    setHasAck(true);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-gate-title"
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-black/80 px-4 py-6 backdrop-blur-md"
    >
      <div
        className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-white/60 bg-white shadow-2xl"
        style={{
          boxShadow:
            '0 0 0 1px rgba(0,0,0,0.08), 0 24px 64px -8px rgba(0,0,0,0.6), 0 8px 16px -4px rgba(0,0,0,0.3)',
        }}
      >
        {/* Hero strip */}
        <div className="border-b border-[var(--color-line)] bg-[var(--color-signal-soft)] px-6 py-5">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[var(--color-signal)]/15 text-[var(--color-signal)]">
              <AlertTriangle size={18} strokeWidth={2.25} />
            </span>
            <div>
              <h2
                id="legal-gate-title"
                className="text-[17px] font-semibold leading-tight tracking-tight text-[var(--color-text)]"
              >
                {t('legal_gate.title')}
              </h2>
              <p className="mt-0.5 text-[12.5px] text-[var(--color-signal)]">
                {t('legal_gate.subtitle')}
              </p>
            </div>
            <div className="ml-auto"><LanguageSwitcher compact /></div>
          </div>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto px-6 py-5 text-[13.5px] leading-relaxed text-[var(--color-text-2)]">
          <p className="text-[14px] text-[var(--color-text)]">
            {t('legal_gate.intro')}
          </p>

          <h3 className="mt-5 text-[13px] font-semibold uppercase tracking-wide text-[var(--color-text-3)]">
            {t('legal_gate.what_not_title')}
          </h3>
          <ul className="mt-2 list-disc space-y-2 pl-5">
            <li>{t('legal_gate.not_medical')}</li>
            <li>{t('legal_gate.not_official')}</li>
            <li>{t('legal_gate.not_realtime')}</li>
            <li>{t('legal_gate.not_exhaustive')}</li>
            <li>{t('legal_gate.not_travel_substitute')}</li>
          </ul>

          <h3 className="mt-5 text-[13px] font-semibold uppercase tracking-wide text-[var(--color-text-3)]">
            {t('legal_gate.data_accuracy_title')}
          </h3>
          <p className="mt-2">{t('legal_gate.data_accuracy_body')}</p>

          <h3 className="mt-5 text-[13px] font-semibold uppercase tracking-wide text-[var(--color-text-3)]">
            {t('legal_gate.privacy_title')}
          </h3>
          <p className="mt-2">{t('legal_gate.privacy_body')}</p>

          <h3 className="mt-5 text-[13px] font-semibold uppercase tracking-wide text-[var(--color-text-3)]">
            {t('legal_gate.license_title')}
          </h3>
          <p className="mt-2">{t('legal_gate.license_body')}</p>

          <h3 className="mt-5 text-[13px] font-semibold uppercase tracking-wide text-[var(--color-text-3)]">
            {t('legal_gate.liability_title')}
          </h3>
          <p className="mt-2">{t('legal_gate.liability_body')}</p>

          <p className="mt-5 text-[12px] text-[var(--color-text-3)]">
            {t('legal_gate.see_more')}
          </p>

          {/* Acknowledgement checkboxes */}
          <div className="mt-6 flex flex-col gap-2.5 rounded-xl border border-[var(--color-line-strong)] bg-[var(--color-surface-2)] px-4 py-4">
            <label className="flex cursor-pointer items-start gap-2.5 text-[13px]">
              <input
                type="checkbox"
                checked={understandsMedical}
                onChange={(e) => setUnderstandsMedical(e.target.checked)}
                className="mt-0.5 h-4 w-4 cursor-pointer accent-[var(--color-accent)]"
              />
              <span>{t('legal_gate.ack_medical')}</span>
            </label>
            <label className="flex cursor-pointer items-start gap-2.5 text-[13px]">
              <input
                type="checkbox"
                checked={understandsSurveillance}
                onChange={(e) => setUnderstandsSurveillance(e.target.checked)}
                className="mt-0.5 h-4 w-4 cursor-pointer accent-[var(--color-accent)]"
              />
              <span>{t('legal_gate.ack_surveillance')}</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse gap-2 border-t border-[var(--color-line)] bg-[var(--color-surface-2)] px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <a
            href="https://www.who.int/health-topics/hantavirus-pulmonary-syndrome"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[12px] font-medium text-[var(--color-text-3)] hover:text-[var(--color-accent)]"
          >
            {t('legal_gate.decline')} →
          </a>
          <button
            type="button"
            onClick={accept}
            disabled={!canAccept}
            className={
              'inline-flex h-10 items-center justify-center gap-2 rounded-lg px-5 text-[13.5px] font-semibold transition-colors ' +
              (canAccept
                ? 'bg-[var(--color-accent)] text-white shadow-[0_2px_4px_rgba(82,71,229,0.25)] hover:bg-[var(--color-accent)]/90'
                : 'cursor-not-allowed bg-[var(--color-surface)] text-[var(--color-text-4)] ring-1 ring-[var(--color-line)]')
            }
          >
            <ShieldCheck size={14} strokeWidth={2.5} />
            {t('legal_gate.accept')}
          </button>
        </div>
      </div>
    </div>
  );
}
